/**
 * Typed API client.
 *
 * One place that knows about CSRF, error shapes, and the session cookie, so no view has to
 * remember. Two behaviours worth stating:
 *
 *  - **CSRF is read from the cookie**, not stored in JS state. The cookie is deliberately
 *    not HttpOnly (double-submit), so a full page reload keeps working without re-login.
 *  - **A 401 is not an exception.** Views distinguish "not logged in" from "request failed",
 *    so the caller gets a typed result rather than a thrown error for the common case.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail?: unknown,
  ) {
    super(`${code} (${status})`);
    this.name = "ApiError";
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

function readCsrfCookie(): string | null {
  for (const part of document.cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === "tb_csrf") return rest.join("=");
  }
  return null;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);

  if (init.body !== undefined) headers.set("Content-Type", "application/json");

  if (method !== "GET" && method !== "HEAD") {
    const csrf = readCsrfCookie();
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }

  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const body = (payload ?? {}) as { error?: string; issues?: unknown };
    throw new ApiError(response.status, body.error ?? "request_failed", body.issues);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => call<T>(path),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    call<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown): Promise<T> =>
    call<T>(path, { method: "PUT", body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string): Promise<T> => call<T>(path, { method: "DELETE" }),
};

/* -------------------------------------------------------------------------- */
/* Response shapes                                                             */
/* -------------------------------------------------------------------------- */

export interface MeResponse {
  authenticated: boolean;
  user?: { id: string; email: string; role: string; venueId: string | null; name: string };
}

export interface DashboardResponse {
  market: {
    venueId: string;
    seq: number;
    marketOpen: boolean;
    drinkCount: number;
    connections: number;
    indexValue: number;
    pendingSync: number;
    nextAlarmAt: number | null;
    activeEffect: string | null;
    haltedDrinks: number;
    venueHaltedUntil: number | null;
  };
  revenue: {
    orderCount: number;
    unitCount: number;
    revenueCents: number;
    completedRefundsCents: number;
    unallocatedRefundCount: number;
    refundsTrackedFrom: number;
    staticRevenueCents: number;
    upliftPct: number;
  };
  square: { syncHealth: { status: string; count: number }[]; webhookBacklog: number };
  gainers: Mover[];
  losers: Mover[];
}

export interface Mover {
  id: string;
  symbol: string;
  name: string;
  priceCents: number;
  basePriceCents: number;
  changePct: number;
}

export interface AdminDrink {
  id: string;
  venueId: string;
  name: string;
  category: string;
  tickerSymbol: string;
  icon: string;
  description: string;
  priceLocked: boolean;
  squareItemId: string | null;
  squareVariationId: string | null;
  basePriceCents: number;
  floorCents: number;
  ceilingCents: number;
  currentPriceCents: number;
  active: boolean;
  sortOrder: number;
}

export interface AdminDisplay {
  id: string;
  name: string;
  settings: Record<string, unknown>;
  revokedAt: number | null;
  lastSeenAt: number | null;
  createdAt: number;
}

export interface AuditEntry {
  id: string;
  actor: string;
  action: string;
  entity: string;
  entityId: string | null;
  ts: number;
}

export interface AdminUser {
  id: string;
  email: string;
  role: string;
  name: string;
  disabledAt: number | null;
  lastLoginAt: number | null;
  createdAt: number;
}
